import { Box } from "@mui/material";
import React, { useMemo } from "react";

import { useReviewPage } from "context/reviewPageContext/reviewPageContext";
import { GroundlightImageQuery } from "features/groundlightImageQuery/GroundlightImageQuery";
import { ExamplesPanel } from "features/reviewPageContent/components/ExamplesPanel";
import { CopyButton } from "new-components/buttons/CopyButton";
import { Disagreement } from "types/disagreements";
import { DATA_TYPE, Posicheck, ReviewBboxDisplayChoices } from "types/imageQueries";
import { getImageQueryFromDisagreement, getImageQueryFromPosicheck } from "utils/imageQueriesUtils";

const disagreementToPosicheck = (disagreement: Disagreement): Posicheck => ({
  id: disagreement.iq_id,
  data_type: DATA_TYPE.DISAGREEMENT,
  owner: disagreement.predictor.owner,
  predictor: disagreement.predictor,
  review_reason: disagreement.challenged_answer.review_reason,
  review_requested_priority: 0,
  image_id: disagreement.challenged_answer.image_id,
  patience_time: disagreement.predictor.patience_time,
  confidence_threshold: disagreement.predictor.confidence_threshold,
  created_at: "",
  human_review_label: null,
  best_prediction: undefined,
  in_review_queue: undefined,
  review_bbox_info: {
    display_bbox: ReviewBboxDisplayChoices.NO_BOXES,
    display_bbox_rois: null,
  },
});

export function ReviewPageContent() {
  const { currentImageQuery, imageQueryId } = useReviewPage();
  const isDisagreement = currentImageQuery?.data_type === DATA_TYPE.DISAGREEMENT;

  const data = useMemo(() => {
    if (isDisagreement) {
      return getImageQueryFromDisagreement(currentImageQuery as Disagreement);
    }
    if (currentImageQuery) {
      return getImageQueryFromPosicheck(currentImageQuery);
    }
    return undefined;
  }, [currentImageQuery, isDisagreement]);

  const currentPosicheck = useMemo(() => {
    if (isDisagreement) {
      return disagreementToPosicheck(currentImageQuery as Disagreement);
    }
    if (currentImageQuery) {
      return currentImageQuery;
    }
    return undefined;
  }, [isDisagreement, currentImageQuery]);

  const reviewBBoxInfo =
    currentImageQuery && "review_bbox_info" in currentImageQuery ? currentImageQuery.review_bbox_info : undefined;

  return (
    <Box
      id="review-page-content"
      sx={{ display: "flex", gap: "20px", justifyContent: "center", height: "calc(100% - 90px)", width: "100%" }}
    >
      <Box
        className="h-full w-[60%]"
        flexGrow={1}
        sx={{
          boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
          borderRadius: "20px",
        }}
      >
        {currentImageQuery && (
          <GroundlightImageQuery
            enablePanZoom
            isEditing
            reviewMode
            detector={currentImageQuery?.predictor}
            data={data}
            reviewBBoxInfo={reviewBBoxInfo}
            roisToDisplay={reviewBBoxInfo?.display_bbox_rois}
          />
        )}
        <Box className="flex gap-2 h-[30px] w-full justify-center mt-[10px]">
          <CopyButton
            iconClassName="h-[12px] w-[12px]"
            buttonClassName="h-[20px] normal-case"
            colorIcon="#C8CDD3"
            valueToCopy={currentImageQuery?.predictor.id || ""}
            text={`Detector ID: ${currentImageQuery?.predictor.id}`}
          />
          <CopyButton
            iconClassName="h-[12px] w-[12px]"
            buttonClassName="h-[20px] normal-case"
            colorIcon="#C8CDD3"
            valueToCopy={imageQueryId || ""}
            text={`Query ID: ${imageQueryId}`}
          />
        </Box>
      </Box>
      {currentPosicheck && (
        <Box
          className="hidden md:flex h-full w-[40%]"
          sx={{
            boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
            borderRadius: "20px",
          }}
        >
          <ExamplesPanel currentPosicheck={currentPosicheck} />
        </Box>
      )}
    </Box>
  );
}
